import { Controller } from "@hotwired/stimulus"

// Infinite-scroll-UP for the chat transcript. The initial render is always
// the newest-N turns (AssistantsController's `.last(50)`), so scrolling to
// the top of a long thread used to just stop, with no way back to older
// history. A sentinel sits above the oldest loaded turn; when it nears the
// top of the scrollable pane, this fetches the previous page and splices it
// in right after the sentinel, then restores scroll position so the
// insertion doesn't yank the view.
export default class extends Controller {
    static targets = ["sentinel"]
    static values = { conversationId: Number, oldestTurnId: Number, url: String }

    connect() {
        if (!("IntersectionObserver" in window) || !this.hasSentinelTarget || !this.hasUrlValue) return

        this._loading = false
        this._done = false
        this.observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this._loadOlder()
        }, { root: this._scrollParent(), rootMargin: "400px 0px 0px 0px" })
        this.observer.observe(this.sentinelTarget)
    }

    disconnect() {
        if (this.observer) this.observer.disconnect()
    }

    // Full-page chat scrolls .assistant-content; the sidebar detail panel
    // (both /messages and /apps/persona) scrolls .msg-detail-scroll.
    _scrollParent() {
        return this.element.closest(".assistant-content") || this.element.closest(".msg-detail-scroll")
    }

    _loadOlder() {
        if (this._loading || this._done) return this._request
        this._loading = true

        const scroller = this._scrollParent()
        const prevScrollHeight = scroller ? scroller.scrollHeight : 0
        const prevScrollTop = scroller ? scroller.scrollTop : 0

        this._request = fetch(`${this.urlValue}?conversation_id=${this.conversationIdValue}&before_id=${this.oldestTurnIdValue}`, {
            headers: { Accept: "text/html" },
            credentials: "same-origin"
        })
            .then((response) => {
                if (!response.ok) throw new Error(`older_turns ${response.status}`)

                const hasMore = response.headers.get("X-Chat-Has-More") === "true"
                const oldestId = response.headers.get("X-Chat-Oldest-Turn-Id")
                return response.text().then((html) => ({ html, hasMore, oldestId }))
            })
            .then(({ html, hasMore, oldestId }) => {
                this._loading = false

                if (html.trim()) {
                    const template = document.createElement("template")
                    template.innerHTML = html
                    this.sentinelTarget.after(template.content)
                }

                if (oldestId) this.oldestTurnIdValue = Number(oldestId)
                if (!hasMore) this._stopObserving()

                if (scroller) {
                    requestAnimationFrame(() => {
                        scroller.scrollTop = prevScrollTop + (scroller.scrollHeight - prevScrollHeight)
                    })
                }
            })
            .catch(() => {
                // A failed fetch (a network hiccup, a transient 500) is not
                // the same signal as `hasMore === false`, but used to be
                // treated as one: _stopObserving() disconnects the
                // IntersectionObserver, which is permanent for the rest of
                // this page view — one bad request and "load older messages"
                // was gone for good, with nothing telling the reader why.
                // Clear the loading flag and leave the observer attached so
                // scrolling the sentinel back into view just retries.
                this._loading = false
            })
        return this._request
    }

    // Also used by the outline to reach a landmark outside the loaded window.
    async reveal(turnId) {
        const selector = `[data-turn-id="${Number(turnId)}"]`
        while (!this.element.querySelector(selector) && !this._done) {
            const before = this.oldestTurnIdValue
            await this._loadOlder()
            if (before === this.oldestTurnIdValue) break
        }
        await new Promise(requestAnimationFrame)
        return this.element.querySelector(selector)
    }

    _stopObserving() {
        this._done = true
        if (this.observer) this.observer.disconnect()
    }
}
